import { useGlobalLoader } from "../../../src/contexts/GlobalLoaderContext";
import React, { useState, useRef, useEffect } from "react";

interface HistoryFiltersProps {
  initialFilters: FilterState;
  closeDropdown: () => void;
  onApplyFilters: (filters: FilterState) => void; // Callback to pass filters to parent
}

interface FilterState {
  requestedFromDate?: string;
  requestedToDate?: string;
  answeredFromDate?: string;
  answeredToDate?: string;
  status?: string;
}

const HistoryFilters: React.FC<HistoryFiltersProps> = ({
  initialFilters,
  closeDropdown,
  onApplyFilters,
}) => {
  const { showLoader, hideLoader } = useGlobalLoader();
  const [filters, setFilters] = useState<FilterState>(initialFilters);
  useEffect(() => {
    setFilters(initialFilters);
  }, [initialFilters]);
  // Create refs for date inputs
  const requestedFromDateRef = useRef<HTMLInputElement>(null);
  const requestedToDateRef = useRef<HTMLInputElement>(null);
  const answeredFromDateRef = useRef<HTMLInputElement>(null);
  const answeredToDateRef = useRef<HTMLInputElement>(null);

  // Function to format date from yyyy-mm-dd to dd/mm/yyyy
  const formatDateForDisplay = (dateString: string): string => {
    if (!dateString) return "";

    try {
      const date = new Date(dateString);
      const day = date.getDate().toString().padStart(2, "0");
      const month = (date.getMonth() + 1).toString().padStart(2, "0");
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    } catch (e) {
      return dateString;
    }
  };

  // Function to parse displayed date (dd/mm/yyyy) back to yyyy-mm-dd format for internal use
  const parseDisplayDate = (displayDate: string): string => {
    if (!displayDate || !displayDate.includes("/")) return "";

    const [day, month, year] = displayDate.split("/");
    return `${year}-${month}-${day}`;
  };

  const handleInputChange = (field: keyof FilterState, value: string) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleDateChange = (
    field:
      | "requestedFromDate"
      | "requestedToDate"
      | "answeredFromDate"
      | "answeredToDate",
    value: string
  ) => {
    // Store the date in ISO format (yyyy-mm-dd) internally
    setFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const clearDateRange = (type: "requested" | "answered") => {
    if (type === "requested") {
      setFilters((prev) => ({
        ...prev,
        requestedFromDate: "",
        requestedToDate: "",
      }));
    } else {
      setFilters((prev) => ({
        ...prev,
        answeredFromDate: "",
        answeredToDate: "",
      }));
    }
  };

  const clearAllFilters = () => {
    const empty: FilterState = {
      requestedFromDate: "",
      requestedToDate: "",
      answeredFromDate: "",
      answeredToDate: "",
      status: "",
    };
    setFilters(empty);
    onApplyFilters(empty);
    // Keep the popup open so the reset fields (and refreshed results) are
    // visible — otherwise "Clear All" just closes the popup and is
    // indistinguishable from "Cancel". Use Cancel / the X to dismiss.
  };

  const handleApply = () => {
    // Pass the current filters to the parent component
    onApplyFilters(filters);
    closeDropdown();
  };

  return (
    <div className="bg-white divide-y divide-gray-100 rounded-lg w-64 sm:w-96 max-w-[calc(100vw-1rem)] max-h-[calc(100vh-7rem)] overflow-y-auto">
      <div
        className="px-3 text-sm text-gray-700"
        aria-labelledby="dropdownDefaultButton-3"
      >
        <div className="flex items-center justify-between pt-3 mx-3 rounded-t border-b border-lightgray-100 pb-3">
          <h3 className="text-base font-semibold text-grease-100">Filters</h3>
          <button
            type="button"
            onClick={closeDropdown}
            className="text-gray-400 bg-transparent rounded-lg text-sm w-8 h-8 ms-auto inline-flex justify-center items-center"
            data-modal-hide="default-modal"
          >
            <svg
              className="w-3 h-3"
              aria-hidden="true"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 14 14"
            >
              <path
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6"
              />
            </svg>
            <span className="sr-only">Close dropdown</span>
          </button>
        </div>

        {/* Requested Date */}
        <div className="bg-indigo-200 bg-opacity-25 rounded-lg mt-3 p-3 mx-3 mb-3">
          <p className="text-sm">Requested Date</p>
          <div className="flex sm:flex-nowrap flex-wrap items-center gap-3 mt-2">
            <div className="">
              {/* <div className="relative">
                                <input
                                    ref={requestedFromDateRef}
                                    type="text"
                                    className="block w-full p-2.5 pr-10 rounded-lg bg-white text-xs text-grease-100 placeholder-gray-400 focus:outline-none"
                                    placeholder="dd/mm/yyyy"
                                    value={formatDateForDisplay(filters.requestedFromDate || '')}
                                    onChange={(e) => {
                                        // Handle manual input in dd/mm/yyyy format
                                        const displayValue = e.target.value;
                                        handleInputChange('requestedFromDate', displayValue);
                                    }}
                                    onFocus={(e) => {
                                        e.target.type = "date";
                                        // Clear display value when focusing to show the date picker properly
                                        if (filters.requestedFromDate) {
                                            const isoDate = parseDisplayDate(formatDateForDisplay(filters.requestedFromDate));
                                            e.target.value = isoDate;
                                        }
                                        e.target.focus();
                                    }}
                                    onBlur={(e) => {
                                        if (!e.target.value) {
                                            e.target.type = "text";
                                            e.target.placeholder = "dd/mm/yyyy";
                                        } else {
                                            // Convert the date value to our internal format and then to display format
                                            handleDateChange('requestedFromDate', e.target.value);
                                            e.target.type = "text";
                                        }
                                    }}
                                />
                                <button
                                    className="absolute inset-y-0 end-0 flex items-center z-20 px-3 cursor-pointer text-gray-400 rounded-e-md focus:outline-none"
                                    onClick={() => {
                                        if (requestedFromDateRef.current) {
                                            requestedFromDateRef.current.type = "date";
                                            requestedFromDateRef.current.showPicker();
                                        }
                                    }}
                                >
                                    <i className="st-calendar text-lightgray-200"></i>
                                </button>
                            </div> */}
              <div className="relative">
                <input
                  ref={requestedFromDateRef}
                  type="date"
                  className="block w-full p-2.5 pr-10 rounded-lg bg-white text-xs text-grease-100 placeholder-gray-400 focus:outline-none"
                  value={filters.requestedFromDate || ""}
                  onChange={(e) =>
                    handleDateChange("requestedFromDate", e.target.value)
                  }
                  max={filters.requestedToDate || undefined}
                />
                <button
                  type="button"
                  className="absolute inset-y-0 end-0 flex items-center z-20 px-3 cursor-pointer text-gray-400 rounded-e-md focus:outline-none"
                  onClick={() => requestedFromDateRef.current?.showPicker()}
                >
                  <i className="st-calendar text-lightgray-200"></i>
                </button>
              </div>
            </div>
            <div className="">
              {/* <div className="relative">
                <input
                  ref={requestedToDateRef}
                  type="text"
                  className="block w-full p-2.5 pr-10 rounded-lg bg-white text-xs text-grease-100 placeholder-gray-400 focus:outline-none"
                  placeholder="dd/mm/yyyy"
                  value={formatDateForDisplay(filters.requestedToDate || "")}
                  onChange={(e) => {
                    // Handle manual input in dd/mm/yyyy format
                    const displayValue = e.target.value;
                    handleInputChange("requestedToDate", displayValue);
                  }}
                  onFocus={(e) => {
                    e.target.type = "date";
                    // Clear display value when focusing to show the date picker properly
                    if (filters.requestedToDate) {
                      const isoDate = parseDisplayDate(
                        formatDateForDisplay(filters.requestedToDate)
                      );
                      e.target.value = isoDate;
                    }
                    e.target.focus();
                  }}
                  onBlur={(e) => {
                    if (!e.target.value) {
                      e.target.type = "text";
                      e.target.placeholder = "dd/mm/yyyy";
                    } else {
                      // Convert the date value to our internal format and then to display format
                      handleDateChange("requestedToDate", e.target.value);
                      e.target.type = "text";
                    }
                  }}
                />
                <button
                  className="absolute inset-y-0 end-0 flex items-center z-20 px-3 cursor-pointer text-gray-400 rounded-e-md focus:outline-none"
                  onClick={() => {
                    if (requestedToDateRef.current) {
                      requestedToDateRef.current.type = "date";
                      requestedToDateRef.current.showPicker();
                    }
                  }}
                >
                  <i className="st-calendar text-lightgray-200"></i>
                </button>
              </div> */}
              <div className="relative">
                <input
                  ref={requestedToDateRef}
                  type="date"
                  className="block w-full p-2.5 pr-10 rounded-lg bg-white text-xs text-grease-100 placeholder-gray-400 focus:outline-none"
                  value={filters.requestedToDate || ""}
                  onChange={(e) =>
                    handleDateChange("requestedToDate", e.target.value)
                  }
                  min={filters.requestedFromDate || undefined}
                />
                <button
                  type="button"
                  className="absolute inset-y-0 end-0 flex items-center z-20 px-3 cursor-pointer text-gray-400 rounded-e-md focus:outline-none"
                  onClick={() => requestedToDateRef.current?.showPicker()}
                >
                  <i className="st-calendar text-lightgray-200"></i>
                </button>
              </div>
            </div>
            {/* <div className="">
              <button
                onClick={() => clearDateRange("requested")}
                className="bg-primaryblue-50 bg-opacity-25 rounded-md h-10 w-10 flex items-center justify-center"
              >
                <i className="st-delete text-lightgray-200"></i>
              </button>
            </div> */}
          </div>
        </div>

        {/* Answered Date  */}
        <div className="bg-indigo-200 bg-opacity-25 rounded-lg mt-3 p-3 mx-3 mb-3">
          <p className="text-sm">Answered Date</p>
          <div className="flex sm:flex-nowrap flex-wrap items-center gap-3 mt-2">
            <div className="">
              {/* <div className="relative">
                <input
                  ref={answeredFromDateRef}
                  type="text"
                  className="block w-full p-2.5 pr-10 rounded-lg bg-white text-xs text-grease-100 placeholder-gray-400 focus:outline-none"
                  placeholder="dd/mm/yyyy"
                  value={formatDateForDisplay(filters.answeredFromDate || "")}
                  onChange={(e) => {
                    // Handle manual input in dd/mm/yyyy format
                    const displayValue = e.target.value;
                    handleInputChange("answeredFromDate", displayValue);
                  }}
                  onFocus={(e) => {
                    e.target.type = "date";
                    // Clear display value when focusing to show the date picker properly
                    if (filters.answeredFromDate) {
                      const isoDate = parseDisplayDate(
                        formatDateForDisplay(filters.answeredFromDate)
                      );
                      e.target.value = isoDate;
                    }
                    e.target.focus();
                  }}
                  onBlur={(e) => {
                    if (!e.target.value) {
                      e.target.type = "text";
                      e.target.placeholder = "dd/mm/yyyy";
                    } else {
                      // Convert the date value to our internal format and then to display format
                      handleDateChange("answeredFromDate", e.target.value);
                      e.target.type = "text";
                    }
                  }}
                />
                <button
                  className="absolute inset-y-0 end-0 flex items-center z-20 px-3 cursor-pointer text-gray-400 rounded-e-md focus:outline-none"
                  onClick={() => {
                    if (answeredFromDateRef.current) {
                      answeredFromDateRef.current.type = "date";
                      answeredFromDateRef.current.showPicker();
                    }
                  }}
                >
                  <i className="st-calendar text-lightgray-200"></i>
                </button>
              </div> */}
              <div className="relative">
                <input
                  ref={answeredFromDateRef}
                  type="date"
                  className="block w-full p-2.5 pr-10 rounded-lg bg-white text-xs text-grease-100 placeholder-gray-400 focus:outline-none"
                  value={filters.answeredFromDate || ""}
                  onChange={(e) =>
                    handleDateChange("answeredFromDate", e.target.value)
                  }
                  max={filters.answeredToDate || undefined}
                />
                <button
                  type="button"
                  className="absolute inset-y-0 end-0 flex items-center z-20 px-3 cursor-pointer text-gray-400 rounded-e-md focus:outline-none"
                  onClick={() => answeredFromDateRef.current?.showPicker()}
                >
                  <i className="st-calendar text-lightgray-200"></i>
                </button>
              </div>
            </div>
            <div className="">
              {/* <div className="relative">
                <input
                  ref={answeredToDateRef}
                  type="text"
                  className="block w-full p-2.5 pr-10 rounded-lg bg-white text-xs text-grease-100 placeholder-gray-400 focus:outline-none"
                  placeholder="dd/mm/yyyy"
                  value={formatDateForDisplay(filters.answeredToDate || "")}
                  onChange={(e) => {
                    // Handle manual input in dd/mm/yyyy format
                    const displayValue = e.target.value;
                    handleInputChange("answeredToDate", displayValue);
                  }}
                  onFocus={(e) => {
                    e.target.type = "date";
                    // Clear display value when focusing to show the date picker properly
                    if (filters.answeredToDate) {
                      const isoDate = parseDisplayDate(
                        formatDateForDisplay(filters.answeredToDate)
                      );
                      e.target.value = isoDate;
                    }
                    e.target.focus();
                  }}
                  onBlur={(e) => {
                    if (!e.target.value) {
                      e.target.type = "text";
                      e.target.placeholder = "dd/mm/yyyy";
                    } else {
                      // Convert the date value to our internal format and then to display format
                      handleDateChange("answeredToDate", e.target.value);
                      e.target.type = "text";
                    }
                  }}
                />
                <button
                  className="absolute inset-y-0 end-0 flex items-center z-20 px-3 cursor-pointer text-gray-400 rounded-e-md focus:outline-none"
                  onClick={() => {
                    if (answeredToDateRef.current) {
                      answeredToDateRef.current.type = "date";
                      answeredToDateRef.current.showPicker();
                    }
                  }}
                >
                  <i className="st-calendar text-lightgray-200"></i>
                </button>
              </div> */}
              <div className="relative w-full sm:w-auto">
                <input
                  ref={answeredToDateRef}
                  type="date"
                  className="block w-full p-2.5 pr-10 rounded-lg bg-white text-xs text-grease-100 placeholder-gray-400 focus:outline-none"
                  value={filters.answeredToDate || ""}
                  onChange={(e) =>
                    handleDateChange("answeredToDate", e.target.value)
                  }
                  min={filters.answeredFromDate || undefined}
                />
                <button
                  type="button"
                  className="absolute inset-y-0 end-0 flex items-center z-20 px-3 cursor-pointer text-gray-400 rounded-e-md focus:outline-none"
                  onClick={() => answeredToDateRef.current?.showPicker()}
                >
                  <i className="st-calendar text-lightgray-200"></i>
                </button>
              </div>
            </div>
            {/* <div className="">
              <button
                onClick={() => clearDateRange("answered")}
                className="bg-primaryblue-50 bg-opacity-25 rounded-md h-10 w-10 flex items-center justify-center"
              >
                <i className="st-delete text-lightgray-200"></i>
              </button>
            </div> */}
          </div>
        </div>

        {/* Status */}
        <div className="bg-indigo-200 bg-opacity-25 rounded-lg mt-3 p-3 mx-3 mb-3 border-b border-lightgray-100">
          <p className="text-sm">Select Status</p>
          <div className="flex sm:flex-nowrap flex-wrap items-center gap-3 mt-2">
            <div className="w-full">
              <select
                value={filters.status}
                onChange={(e) => handleInputChange("status", e.target.value)}
                className="block w-full border-none p-2.5 rounded-lg bg-white text-xs text-grease-100 placeholder-gray-400 focus:outline-none"
              >
                <option value="">All</option>
                {/* <option value="Pending">Pending</option> */}
                <option value="Answered">Answered</option>
                <option value="Expired">Expired</option>
              </select>
            </div>
            {/* <div className="">
              <button
                onClick={() => {
                  const empty: FilterState = { ...filters, status: "" };
                  setFilters(empty);
                  onApplyFilters(empty);
                  closeDropdown();
                }}
                className="bg-primaryblue-50 bg-opacity-25 rounded-md h-10 w-10 flex items-center justify-center"
              >
                <i className="st-delete text-lightgray-200"></i>
              </button>
            </div> */}
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-between mx-3 mb-3">
          <button
            onClick={closeDropdown}
            className="flex gap-2 px-3 py-2 bg-blue-500 bg-opacity-25 rounded-md"
          >
            <p className="font-medium text-lightgray-200 text-xs">Cancel</p>
          </button>
          <div className="flex items-center gap-4">
            <button
              onClick={clearAllFilters}
              className="flex gap-2 px-3 py-2 border border-[#FFBC00] rounded-md"
            >
              <p className="font-medium text-[#FFBC00] text-xs">Clear All</p>
            </button>
            <button
              onClick={handleApply}
              className="flex gap-2 px-3 py-2 bg-[#FFBC00] rounded-md"
            >
              <p className="font-medium text-[#F5F9FE] text-xs">Apply</p>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HistoryFilters;
